import type { ReactNode } from 'react';
import { Megaphone, Monitor } from 'lucide-react';

export type PreviewDevice = 'iphone' | 'android' | 'pc';

export type DeviceOption = {
  id: PreviewDevice;
  label: string;
};

const PREVIEW_LABELS: Record<PreviewDevice, string> = {
  iphone: 'iPhone preview',
  android: 'Android preview',
  pc: 'PC preview',
};

export function PreviewHeader({
  device,
  switcher,
}: {
  device: PreviewDevice;
  switcher: ReactNode;
}) {
  return (
    <header className="sn-preview-header">
      <p className="sn-preview-header-label">
        <Monitor className="size-4" strokeWidth={1.75} aria-hidden />
        <span>{PREVIEW_LABELS[device]}</span>
      </p>
      {switcher}
    </header>
  );
}

export function DeviceSwitcher({
  devices,
  value,
  onChange,
  ariaLabel,
}: {
  devices: DeviceOption[];
  value: PreviewDevice;
  onChange: (device: PreviewDevice) => void;
  ariaLabel: string;
}) {
  return (
    <div className="sn-device-switcher" role="group" aria-label={ariaLabel}>
      {devices.map((device) => (
        <button
          key={device.id}
          type="button"
          onClick={() => onChange(device.id)}
          className={[
            'sn-device-switcher__btn',
            value === device.id ? 'is-active' : '',
          ]
            .filter(Boolean)
            .join(' ')}
          aria-pressed={value === device.id}
        >
          {device.label}
        </button>
      ))}
    </div>
  );
}

export function IntroCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <aside className="sn-intro-card">
      <span className="sn-intro-card__icon" aria-hidden>
        <Megaphone className="size-4" strokeWidth={2} />
      </span>
      <div>
        <h2 className="sn-intro-card__title">{title}</h2>
        <div className="sn-intro-card__body">{children}</div>
      </div>
    </aside>
  );
}

export function NewsletterSection({
  title,
  intro,
  children,
}: {
  title: string;
  intro?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <section className="sn-newsletter-section">
      <h3 className="sn-newsletter-section__title">{title}</h3>
      {intro ? <div className="sn-newsletter-section__intro">{intro}</div> : null}
      {children}
    </section>
  );
}

export type ArticleListItem = {
  id: string;
  summary: ReactNode;
  href?: string;
  linkLabel?: string;
};

export function ArticleList({ items }: { items: ArticleListItem[] }) {
  return (
    <ul className="sn-article-list">
      {items.map((item) => (
        <li key={item.id}>
          {item.summary}
          {item.href ? (
            <>
              {' — '}
              <a href={item.href} target="_blank" rel="noopener noreferrer">
                {item.linkLabel ?? item.href}
              </a>
            </>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

/** Soft email preview polish — mirrors IntroCard / NewsletterSection / ArticleList visuals. */
export function buildEmailPreviewPolishCss(): string {
  return `
    <style id="sn-email-polish">
      :root {
        color-scheme: light;
      }
      html, body {
        margin: 0;
        padding: 0;
        background: #ffffff;
        color: #0f172a;
        font-family: Inter, Manrope, ui-sans-serif, system-ui, -apple-system, 'Segoe UI', Roboto, sans-serif;
        font-size: 14px;
        font-weight: 400;
        line-height: 1.65;
        overflow-x: hidden;
        overflow-wrap: break-word;
        scrollbar-width: thin;
        scrollbar-color: #94a3b8 transparent;
      }
      body {
        box-sizing: border-box;
        padding: 20px 24px 28px;
        max-width: 100%;
      }
      *, *::before, *::after { box-sizing: border-box; }
      a {
        color: #2563eb;
        font-weight: 500;
        text-decoration: none;
        overflow-wrap: anywhere;
        transition: color 160ms ease, text-decoration-color 160ms ease;
      }
      a:hover {
        color: #1d4ed8;
        text-decoration: underline;
        text-underline-offset: 2px;
      }
      a:focus-visible {
        outline: 2px solid #2563eb;
        outline-offset: 2px;
        border-radius: 2px;
      }
      h1, h2, h3, h4 {
        color: #0f172a;
        line-height: 1.3;
        font-weight: 700;
      }
      h1 { font-size: 20px; }
      h2 { font-size: 18px; }
      h3 { font-size: 20px; color: #0f172a; }
      p {
        margin: 0 0 1em;
        color: #475569;
        font-size: 14px;
        line-height: 1.65;
        max-width: 72ch;
      }
      /* Intro-like first cards / tinted blocks */
      body > table:first-child,
      body > div:first-child,
      .intro, .intro-card, .header-card,
      [class*="intro"],
      [style*="background:#"],
      td[bgcolor],
      td[style*="background"] {
        border-radius: 16px !important;
      }
      ul, ol {
        margin: 16px 0 0;
        padding-left: 1.35rem;
      }
      li {
        margin: 0 0 10px;
        color: #0f172a;
        font-size: 14px;
        line-height: 1.65;
        padding-right: 8px;
      }
      li::marker { color: #64748b; }
      /* Topic / section accent (common digest patterns) */
      .topic, .section, .category, [class*="topic"], [class*="section"] {
        margin-top: 28px;
        border-left: 3.5px solid #2563eb;
        border-radius: 2px;
        padding: 4px 8px 4px 20px;
      }
      blockquote {
        margin: 1.25em 0;
        border-left: 3.5px solid #2563eb;
        border-radius: 2px;
        padding: 4px 0 4px 18px;
        color: #475569;
      }
      img { max-width: 100%; height: auto; }
      table { max-width: 100%; border-collapse: collapse; }
      ::-webkit-scrollbar { width: 8px; height: 8px; }
      ::-webkit-scrollbar-track { background: transparent; }
      ::-webkit-scrollbar-thumb {
        border-radius: 999px;
        background: #94a3b8;
      }
      ::-webkit-scrollbar-thumb:hover { background: #2563eb; }
      ::-webkit-scrollbar-corner { background: transparent; }
    </style>
  `;
}
